import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Layers, Search } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { isDisplayableCode } from '@/lib/utils/filter-search'
import { useSegmentFilterQuestionList } from '@/hooks/useSegmentFilterQuestionList'

import type { SegmentFilterQuestion, SegmentFilterOption } from '@/lib/utils/segment-filter-questions'

export interface SegmentFilterQuestionPickerValue {
  sourceColumn?: string
  groupId?: string
}

interface SegmentFilterQuestionPickerProps {
  questions: Array<SegmentFilterQuestion>
  value: SegmentFilterQuestionPickerValue
  /** Called when the user clicks a group label (selecting the whole group). */
  onSelectGroup: (question: SegmentFilterQuestion) => void
  /** Called when the user clicks a single question or a specific variant inside a group. */
  onSelectColumn: (sourceColumn: string, parentQuestion: SegmentFilterQuestion) => void
  placeholder?: string
  searchPlaceholder?: string
  onSearchChange?: (query: string) => void
  disabled?: boolean
  triggerClassName?: string
  /** data-testid applied to the trigger button */
  'data-testid'?: string
}

export function SegmentFilterQuestionPicker(props: SegmentFilterQuestionPickerProps) {
  const { t } = useTranslation('app')
  const {
    questions,
    value,
    onSelectGroup,
    onSelectColumn,
    placeholder = t('audienceCreate.chooseColumn'),
    searchPlaceholder = t('audienceCreate.searchColumns'),
    onSearchChange,
    disabled = false,
    triggerClassName,
  } = props

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null)

  const { questionsByCategory, categoryLabel } = useSegmentFilterQuestionList({
    questions,
    search,
  })

  const handleOpenChange = (o: boolean) => {
    setOpen(o)
    if (!o) setSearch('')
  }

  const handleSelectGroup = (question: SegmentFilterQuestion) => {
    onSelectGroup(question)
    setOpen(false)
    setSearch('')
  }

  const handleSelectColumn = (sourceColumn: string, parentQuestion: SegmentFilterQuestion) => {
    onSelectColumn(sourceColumn, parentQuestion)
    setOpen(false)
    setSearch('')
  }

  const triggerLabel = getTriggerLabel(value, questions)

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('w-full min-w-0 justify-between', triggerClassName)}
          disabled={disabled}
          data-testid={props['data-testid']}
        >
          <span className="w-0 grow truncate text-left">
            {triggerLabel || placeholder}
          </span>
          <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <div className="flex items-center border-b px-2 py-1.5">
          <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              onSearchChange?.(e.target.value)
            }}
            autoFocus
          />
        </div>
        <div className="max-h-[280px] overflow-y-auto overscroll-contain p-1">
          {questionsByCategory.length === 0 ? (
            <p className="px-2 py-3 text-center text-sm text-muted-foreground">
              {t('audienceCreate.noColumnsMatch')}
            </p>
          ) : (
            questionsByCategory.map(([category, items], idx) => (
              <div key={category}>
                {idx > 0 ? <div className="-mx-1 my-1 h-px bg-border/40" /> : null}
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  {categoryLabel(category)}
                </div>
                {items.map((item) =>
                  item.kind === 'group'
                    ? renderGroup(item)
                    : renderSingle(item),
                )}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )

  function renderSingle(item: SegmentFilterQuestion) {
    const col = item.options[0]?.sourceColumn
    const isSelected = !value.groupId && value.sourceColumn === col
    return (
      <button
        key={item.id}
        type="button"
        className={cn(
          'w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
          isSelected && 'bg-accent font-medium',
        )}
        onClick={() => handleSelectColumn(col, item)}
      >
        <span className="block truncate">{item.label}</span>
        {isDisplayableCode(col ?? '', item.label) && (
          <span
            data-testid={`segment-filter-question-code-${col}`}
            className="block truncate font-mono text-[10px] text-muted-foreground/50"
          >
            {col}
          </span>
        )}
      </button>
    )
  }

  function renderGroup(item: SegmentFilterQuestion) {
    const isExpanded = expandedGroupId === item.id
    const isGroupSelected = value.groupId === item.id
    const needle = search.trim().toLowerCase()
    const visibleOptions = needle
      ? item.options.filter(
          (o) =>
            item.label.toLowerCase().includes(needle) ||
            o.optionLabel.toLowerCase().includes(needle),
        )
      : item.options

    return (
      <div key={item.id}>
        <div
          className={cn(
            'flex w-full items-center rounded-md hover:bg-accent',
            isGroupSelected && 'bg-accent font-medium',
          )}
        >
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm"
            onClick={() => handleSelectGroup(item)}
          >
            <Layers className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              ({visibleOptions.length})
            </span>
          </button>
          <button
            type="button"
            className="shrink-0 rounded-md px-2 py-1.5 text-muted-foreground hover:text-foreground"
            aria-label={isExpanded ? t('audienceCreate.collapseGroup') : t('audienceCreate.expandGroup')}
            onClick={(e) => {
              e.stopPropagation()
              setExpandedGroupId(isExpanded ? null : item.id)
            }}
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 opacity-60 transition-transform',
                isExpanded && 'rotate-180',
              )}
            />
          </button>
        </div>
        {isExpanded &&
          visibleOptions.map((opt) => {
            const isSelected =
              !value.groupId && value.sourceColumn === opt.sourceColumn
            return (
              <button
                key={opt.sourceColumn}
                type="button"
                className={cn(
                  'w-full rounded-md py-1.5 pl-7 pr-2 text-left text-sm hover:bg-accent',
                  isSelected && 'bg-accent font-medium',
                )}
                onClick={() => handleSelectColumn(opt.sourceColumn, item)}
              >
                {opt.optionLabel}
              </button>
            )
          })}
      </div>
    )
  }
}

function getTriggerLabel(
  value: SegmentFilterQuestionPickerValue,
  questions: Array<SegmentFilterQuestion>,
): string {
  if (value.groupId) {
    const group = questions.find((q) => q.id === value.groupId)
    return group ? `${group.label} (whole group)` : ''
  }
  if (value.sourceColumn) {
    for (const q of questions) {
      if (q.kind === 'single' && q.options[0]?.sourceColumn === value.sourceColumn) {
        return q.label
      }
      if (q.kind === 'group') {
        const opt = q.options.find((o) => o.sourceColumn === value.sourceColumn)
        if (opt) return `${q.label}: ${opt.optionLabel}`
      }
    }
    return value.sourceColumn
  }
  return ''
}
